/**
 * QAPanel — PixiJS Container-based visual QA debug panel overlay.
 *
 * Renders at zIndex 9999 in design coordinates. Collapsible to a small "QA" badge.
 * Provides buttons for scenario triggers, speed control, balance management,
 * and a live stats bar.
 */

import { Container, Graphics, Text, TextStyle, FederatedPointerEvent } from 'pixi.js';
import { QAController } from './QAController';
import { QAStats } from './QAStats';
import { GameConfig } from '../config/GameConfig';
import type { QAScenarioId } from './QAOverrides';

// ── Constants ────────────────────────────────────────────────────────────────

const PANEL_W = 380;
const PAD = 10;
const BTN_H = 30;
const BTN_GAP = 6;
const HEADER_H = 32;

const COL_BG       = 0x1a1a2e;
const COL_SECTION   = 0x16213e;
const COL_BTN       = 0x0f3460;
const COL_BTN_ACTIVE = 0xe94560;
const COL_TEXT      = 0xffffff;
const COL_TEXT_DIM  = 0xaaaaaa;
const COL_BADGE_BG  = 0x0f3460;

const FONT = 'Arial';

function smallStyle(size = 14, color = COL_TEXT): TextStyle {
  return new TextStyle({ fontFamily: FONT, fontSize: size, fill: color });
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function makeRect(
  g: Graphics, x: number, y: number, w: number, h: number,
  fill: number, alpha = 1, radius = 4,
): void {
  g.beginFill(fill, alpha);
  g.drawRoundedRect(x, y, w, h, radius);
  g.endFill();
}

interface BtnDef {
  label: string;
  color?: number;
  onClick: () => void;
}

// ── QAPanel ──────────────────────────────────────────────────────────────────

export class QAPanel {
  public readonly container: Container;

  private _ctrl: QAController;
  private _stats: QAStats;

  // sub-containers
  private _panel: Container;   // full expanded panel
  private _badge: Container;   // collapsed badge

  // dynamic text
  private _statsText!: Text;
  private _stateText!: Text;

  // track active scenario / speed for highlighting
  private _activeScenario: QAScenarioId | null = null;
  private _activeSpeed = 1;
  private _stickyOn = false;
  private _stateExpanded = false;

  // dragging
  private _dragging = false;
  private _dragOffset = { x: 0, y: 0 };

  // all clickable button graphics (for re-coloring)
  private _scenarioBtns: Map<QAScenarioId, Graphics> = new Map();
  private _speedBtns: Map<number, Graphics> = new Map();
  private _stickyBtn: Graphics | null = null;

  constructor(qaController: QAController, qaStats: QAStats) {
    this._ctrl = qaController;
    this._stats = qaStats;

    this.container = new Container();
    this.container.zIndex = 9999;
    this.container.sortableChildren = true;
    this.container.visible = false; // start hidden

    this._panel = new Container();
    this._badge = new Container();

    this.container.addChild(this._panel);
    this.container.addChild(this._badge);

    this._buildBadge();
    this._buildPanel();
    this._positionAtRight();

    // show badge, hide panel initially
    this._panel.visible = false;
    this._badge.visible = true;

    // listen for toggle from controller (Ctrl+Shift+Q)
    this._ctrl.onToggle((visible: boolean) => {
      if (visible) {
        this.container.visible = true;
        this._panel.visible = true;
        this._badge.visible = false;
      } else {
        this._panel.visible = false;
        this._badge.visible = true;
      }
    });

    // update stats on each spin
    this._ctrl.onSpin(() => {
      this._refreshStats();
    });
  }

  // ── Badge (collapsed) ──────────────────────────────────────────

  private _buildBadge(): void {
    const bg = new Graphics();
    makeRect(bg, 0, 0, 50, 28, COL_BADGE_BG, 0.9);
    this._badge.addChild(bg);

    const label = new Text('QA', smallStyle(14, COL_TEXT));
    label.x = 12; label.y = 5;
    this._badge.addChild(label);

    this._badge.eventMode = 'static';
    this._badge.cursor = 'pointer';
    this._badge.on('pointerdown', () => {
      this._ctrl.toggle(); // will fire onToggle -> show panel
    });
  }

  // ── Full panel ─────────────────────────────────────────────────

  private _buildPanel(): void {
    let curY = 0;

    // Background
    const bg = new Graphics();
    this._panel.addChild(bg);

    // 1. Header
    curY = this._buildHeader(curY);

    // 2. Stats bar
    curY = this._buildStatsBar(curY);

    // 3. Feature triggers
    curY = this._buildSectionLabel('FEATURE TRIGGERS', curY);
    curY = this._buildFeatureButtons(curY);

    // 4. Speed control
    curY = this._buildSectionLabel('SPEED', curY);
    curY = this._buildSpeedButtons(curY);

    // 5. Balance
    curY = this._buildSectionLabel('BALANCE', curY);
    curY = this._buildBalanceButtons(curY);

    // 6. State inspector
    curY = this._buildSectionLabel('STATE INSPECTOR', curY);
    curY = this._buildStateInspector(curY);

    curY += PAD;

    // Draw background now that we know total height
    makeRect(bg, 0, 0, PANEL_W, curY, COL_BG, 0.9, 6);
  }

  // ── Header ─────────────────────────────────────────────────────

  private _buildHeader(y: number): number {
    const headerBg = new Graphics();
    makeRect(headerBg, 0, y, PANEL_W, HEADER_H, COL_SECTION, 1, 6);
    this._panel.addChild(headerBg);

    const title = new Text('QA PANEL', smallStyle(16, COL_TEXT));
    title.x = PAD; title.y = y + 6;
    this._panel.addChild(title);

    // Minimize button [−]
    const minBtn = this._makeTextButton('−', 24, 24, COL_BTN, () => {
      this._ctrl.toggle(); // toggle off -> badge
    });
    minBtn.x = PANEL_W - 60; minBtn.y = y + 4;
    this._panel.addChild(minBtn);

    // Close button [×]
    const closeBtn = this._makeTextButton('×', 24, 24, COL_BTN_ACTIVE, () => {
      this.container.visible = false;
    });
    closeBtn.x = PANEL_W - 30; closeBtn.y = y + 4;
    this._panel.addChild(closeBtn);

    // Drag support on header background
    headerBg.eventMode = 'static';
    headerBg.cursor = 'grab';
    headerBg.on('pointerdown', (e: FederatedPointerEvent) => {
      this._dragging = true;
      const pos = this.container.parent?.toLocal(e.global) ?? e.global;
      this._dragOffset.x = pos.x - this.container.x;
      this._dragOffset.y = pos.y - this.container.y;
      headerBg.cursor = 'grabbing';
    });
    const stage = headerBg;
    const onMove = (e: FederatedPointerEvent) => {
      if (!this._dragging) return;
      const pos = this.container.parent?.toLocal(e.global) ?? e.global;
      this.container.x = pos.x - this._dragOffset.x;
      this.container.y = pos.y - this._dragOffset.y;
    };
    const onUp = () => {
      this._dragging = false;
      headerBg.cursor = 'grab';
    };
    headerBg.on('globalpointermove', onMove);
    headerBg.on('pointerup', onUp);
    headerBg.on('pointerupoutside', onUp);

    return y + HEADER_H + 2;
  }

  // ── Stats bar ──────────────────────────────────────────────────

  private _buildStatsBar(y: number): number {
    const barBg = new Graphics();
    makeRect(barBg, PAD, y, PANEL_W - PAD * 2, 22, COL_SECTION, 0.8, 3);
    this._panel.addChild(barBg);

    this._statsText = new Text('RTP: N/A | Spins: 0', smallStyle(12, COL_TEXT_DIM));
    this._statsText.x = PAD + 6;
    this._statsText.y = y + 3;
    this._panel.addChild(this._statsText);

    return y + 28;
  }

  private _refreshStats(): void {
    const s = this._stats.getStats();
    const rtp = this._stats.getRTPString();
    const bal = this._ctrl.getForceBalance();
    const balStr = bal !== undefined ? ` Bal:${bal.toFixed(0)}` : '';
    this._statsText.text = `RTP:${rtp} Spins:${s.totalSpins} ${this._stats.getFeatureString()}${balStr}`;
    this._refreshStateInspector();
  }

  // ── Section label ──────────────────────────────────────────────

  private _buildSectionLabel(text: string, y: number): number {
    y += 4;
    const label = new Text(text, smallStyle(11, COL_TEXT_DIM));
    label.x = PAD;
    label.y = y;
    this._panel.addChild(label);
    return y + 18;
  }

  // ── Feature trigger buttons ────────────────────────────────────

  private _buildFeatureButtons(y: number): number {
    const rows: { label: string; id: QAScenarioId; color?: number }[][] = [
      [
        { label: 'Jackpot Pick', id: 'jackpotPick' },
        { label: 'Free Games', id: 'freeGames' },
      ],
      [
        { label: 'Rush 3', id: 'rushFever3' },
        { label: 'Rush 5', id: 'rushFever5' },
        { label: 'Rush 7', id: 'rushFever7' },
        { label: 'Rush 9', id: 'rushFever9' },
      ],
      [
        { label: 'Amazing', id: 'bigWinAmazing' },
        { label: 'Awesome', id: 'bigWinAwesome' },
        { label: 'Incredible', id: 'bigWinIncredible' },
      ],
      [
        { label: 'Dead Spin', id: 'deadSpin' },
        { label: 'Small Win', id: 'smallWin' },
      ],
      [
        { label: 'JP T9', id: 'jackpotPickTier9' },
        { label: 'JP NoMatch', id: 'jackpotPickNoMatch' },
        { label: 'FG+JP', id: 'freeGamesWithJP' },
      ],
    ];

    for (const row of rows) {
      const btnW = (PANEL_W - PAD * 2 - BTN_GAP * (row.length - 1)) / row.length;
      let x = PAD;
      for (const item of row) {
        const btn = this._makeTextButton(item.label, btnW, BTN_H, COL_BTN, () => {
          this._activeScenario = item.id;
          this._ctrl.applyScenario(item.id);
          this._highlightScenario(item.id);
        });
        btn.x = x;
        btn.y = y;
        this._panel.addChild(btn);
        this._scenarioBtns.set(item.id, btn.getChildAt(0) as Graphics);
        x += btnW + BTN_GAP;
      }
      y += BTN_H + BTN_GAP;
    }

    // Sticky checkbox row
    const stickyBtnW = 80;
    const stickyBtn = this._makeTextButton(
      this._stickyOn ? '[x] Sticky' : '[ ] Sticky',
      stickyBtnW, BTN_H, COL_BTN,
      () => {
        this._stickyOn = !this._stickyOn;
        this._ctrl.setOverride({ sticky: this._stickyOn });
        // update label
        const txt = stickyBtn.getChildAt(1) as Text;
        txt.text = this._stickyOn ? '[x] Sticky' : '[ ] Sticky';
        const bg = stickyBtn.getChildAt(0) as Graphics;
        bg.clear();
        makeRect(bg, 0, 0, stickyBtnW, BTN_H, this._stickyOn ? COL_BTN_ACTIVE : COL_BTN, 1, 4);
      },
    );
    stickyBtn.x = PAD;
    stickyBtn.y = y;
    this._panel.addChild(stickyBtn);
    this._stickyBtn = stickyBtn.getChildAt(0) as Graphics;

    // Clear overrides button
    const clearW = 100;
    const clearBtn = this._makeTextButton('Clear All', clearW, BTN_H, 0x8b0000, () => {
      this._ctrl.clearOverrides();
      this._activeScenario = null;
      this._stickyOn = false;
      this._activeSpeed = 1;
      this._highlightScenario(null);
      this._highlightSpeed(1);
      // reset sticky label
      const txt = stickyBtn.getChildAt(1) as Text;
      txt.text = '[ ] Sticky';
      const bg = stickyBtn.getChildAt(0) as Graphics;
      bg.clear();
      makeRect(bg, 0, 0, stickyBtnW, BTN_H, COL_BTN, 1, 4);
    });
    clearBtn.x = PAD + stickyBtnW + BTN_GAP;
    clearBtn.y = y;
    this._panel.addChild(clearBtn);

    y += BTN_H + BTN_GAP;
    return y;
  }

  // ── Speed buttons ──────────────────────────────────────────────

  private _buildSpeedButtons(y: number): number {
    const speeds = [
      { label: '1x', value: 1 },
      { label: '2x', value: 2 },
      { label: '4x', value: 4 },
      { label: 'Inst', value: 100 },
    ];
    const btnW = (PANEL_W - PAD * 2 - BTN_GAP * (speeds.length - 1)) / speeds.length;
    let x = PAD;
    for (const sp of speeds) {
      const btn = this._makeTextButton(sp.label, btnW, BTN_H, sp.value === 1 ? COL_BTN_ACTIVE : COL_BTN, () => {
        this._activeSpeed = sp.value;
        this._ctrl.setOverride({
          speedMultiplier: sp.value,
          skipAnimations: sp.value >= 100,
        });
        this._highlightSpeed(sp.value);
      });
      btn.x = x;
      btn.y = y;
      this._panel.addChild(btn);
      this._speedBtns.set(sp.value, btn.getChildAt(0) as Graphics);
      x += btnW + BTN_GAP;
    }
    y += BTN_H + BTN_GAP;
    return y;
  }

  // ── Balance buttons ────────────────────────────────────────────

  private _buildBalanceButtons(y: number): number {
    const actions: BtnDef[] = [
      {
        label: '+1K',
        onClick: () => {
          const cur = this._ctrl.getForceBalance() ?? GameConfig.STARTING_BALANCE;
          this._ctrl.setOverride({ forceBalance: cur + 1000 });
          this._refreshStats();
        },
      },
      {
        label: '+5K',
        onClick: () => {
          const cur = this._ctrl.getForceBalance() ?? GameConfig.STARTING_BALANCE;
          this._ctrl.setOverride({ forceBalance: cur + 5000 });
          this._refreshStats();
        },
      },
      {
        label: 'Reset',
        onClick: () => {
          this._ctrl.setOverride({ forceBalance: GameConfig.STARTING_BALANCE });
          this._refreshStats();
        },
      },
    ];

    const btnW = (PANEL_W - PAD * 2 - BTN_GAP * (actions.length - 1)) / actions.length;
    let x = PAD;
    for (const act of actions) {
      const btn = this._makeTextButton(act.label, btnW, BTN_H, COL_BTN, act.onClick);
      btn.x = x; btn.y = y;
      this._panel.addChild(btn);
      x += btnW + BTN_GAP;
    }
    y += BTN_H + BTN_GAP;
    return y;
  }

  // ── State inspector ────────────────────────────────────────────

  private _buildStateInspector(y: number): number {
    const toggleBtn = this._makeTextButton('▸ Expand', 80, 22, COL_SECTION, () => {
      this._stateExpanded = !this._stateExpanded;
      this._stateText.visible = this._stateExpanded;
      const txt = toggleBtn.getChildAt(1) as Text;
      txt.text = this._stateExpanded ? '▾ Collapse' : '▸ Expand';
      this._refreshStateInspector();
    });
    toggleBtn.x = PAD; toggleBtn.y = y;
    this._panel.addChild(toggleBtn);
    y += 26;

    this._stateText = new Text('', smallStyle(11, COL_TEXT_DIM));
    this._stateText.x = PAD;
    this._stateText.y = y;
    this._stateText.visible = false;
    this._panel.addChild(this._stateText);

    // Reserve space — text will grow
    y += 80;
    return y;
  }

  private _refreshStateInspector(): void {
    if (!this._stateExpanded) return;
    const o = this._ctrl.override;
    const dw = GameConfig.DESIGN_WIDTH_LANDSCAPE;
    const dh = GameConfig.DESIGN_HEIGHT_LANDSCAPE;
    const orient = window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
    const lines = [
      `Design: ${dw}×${dh}  Orient: ${orient}`,
      `Feature: ${o.forceFeature ?? 'none'}  Sticky: ${o.sticky}`,
      `Speed: ${o.speedMultiplier}x  Skip: ${o.skipAnimations}`,
      `Grid: ${o.forceGrid ? 'set' : 'none'}  Balance: ${o.forceBalance ?? 'default'}`,
      `Spins recorded: ${this._ctrl.spinHistory.length}`,
    ];
    this._stateText.text = lines.join('\n');
  }

  // ── Highlight helpers ──────────────────────────────────────────

  private _highlightScenario(active: QAScenarioId | null): void {
    for (const [id, gfx] of this._scenarioBtns) {
      const w = gfx.width;
      gfx.clear();
      makeRect(gfx, 0, 0, w, BTN_H, id === active ? COL_BTN_ACTIVE : COL_BTN, 1, 4);
    }
  }

  private _highlightSpeed(active: number): void {
    for (const [val, gfx] of this._speedBtns) {
      const w = gfx.width;
      gfx.clear();
      makeRect(gfx, 0, 0, w, BTN_H, val === active ? COL_BTN_ACTIVE : COL_BTN, 1, 4);
    }
  }

  // ── Generic button factory ─────────────────────────────────────

  private _makeTextButton(
    label: string, w: number, h: number, fill: number, onClick: () => void,
  ): Container {
    const c = new Container();

    const bg = new Graphics();
    makeRect(bg, 0, 0, w, h, fill, 1, 4);
    c.addChild(bg);

    const txt = new Text(label, smallStyle(12, COL_TEXT));
    txt.anchor.set(0.5);
    txt.x = w / 2;
    txt.y = h / 2;
    c.addChild(txt);

    c.eventMode = 'static';
    c.cursor = 'pointer';
    c.on('pointerdown', onClick);

    return c;
  }

  // ── Positioning ────────────────────────────────────────────────

  private _positionAtRight(): void {
    const designW = GameConfig.DESIGN_WIDTH_LANDSCAPE;
    this.container.x = designW - PANEL_W - 10;
    this.container.y = 10;
  }

  /** Call if design dimensions change (e.g. orientation switch). */
  reposition(): void {
    this._positionAtRight();
  }
}
